import type { FC } from 'react';
import { useEffect } from 'react';
import { Col, Modal, Row, Segmented, Typography } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { PlatformForm as Form, PlatformSelect as Select } from '@/shared/components/forms';
import { preferenceModalBodyStyle } from '@/shared/components/modals/preferenceModalStyles';
import { formatFinancialLineItemLabel } from '@/utilities/helpers/formatFinancialLineItemLabel';

import { FINANCIAL_STATEMENT_SECTIONS } from '../../financialSeries';
import type { FinancialStatementDefaults } from '../../financialSeries';

interface FinancialStatementPreferencesModalProps {
    defaults: FinancialStatementDefaults;
    onCancel: () => void;
    onSave: (defaults: FinancialStatementDefaults) => void;
    open: boolean;
}

const FinancialStatementPreferencesModal: FC<FinancialStatementPreferencesModalProps> = ({
    defaults,
    onCancel,
    onSave,
    open,
}) => {
    const [form] = Form.useForm<FinancialStatementDefaults>();
    const { token } = useAntDesignTheme();

    useEffect(() => {
        if (open) {
            form.setFieldsValue({
                dateRangeYears: defaults.dateRangeYears ?? 15,
                lineItems: defaults.lineItems ?? {},
                period: defaults.period ?? 'annual',
            });
        }
    }, [defaults, form, open]);

    return (
        <Modal
            cancelButtonProps={{ size: 'small' }}
            destroyOnHidden
            okButtonProps={{ size: 'small' }}
            okText='Save defaults'
            open={open}
            styles={{ body: preferenceModalBodyStyle }}
            title='Series Preferences'
            width={16 * 34}
            onCancel={onCancel}
            onOk={() => void form.validateFields().then(onSave)}
        >
            <Typography.Paragraph style={{ marginBottom: '1rem' }} type='secondary'>
                These user-specific defaults apply to financial series in Company Search and Investment Analysis.
            </Typography.Paragraph>
            <Form form={form} layout='vertical'>
                <div style={{ marginBottom: '0.75rem' }}>
                    <small style={{ color: token.colorTextTertiary }}>General defaults</small>
                    <div
                        style={{
                            background: token.colorBgBase,
                            border: `1px dashed ${token.colorBorder}`,
                            borderRadius: token.borderRadius,
                            marginTop: '0.25rem',
                            padding: '0.75rem',
                        }}
                    >
                        <Row gutter={[12, 0]}>
                            <Col span={12}>
                                <Form.Item label='Default time frame' name='dateRangeYears' style={{ marginBottom: 0 }}>
                                    <Select
                                        options={[
                                            { label: 'Past 5 years', value: 5 },
                                            { label: 'Past 10 years', value: 10 },
                                            { label: 'Past 15 years', value: 15 },
                                        ]}
                                        size='small'
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label='Default period' name='period' style={{ marginBottom: 0 }}>
                                    <Segmented
                                        block
                                        options={[
                                            { label: 'Year', value: 'annual' },
                                            { label: 'Quarter', value: 'quarter' },
                                        ]}
                                        size='small'
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>
                </div>
                <div>
                    <small style={{ color: token.colorTextTertiary }}>Default line items</small>
                    <div
                        style={{
                            background: token.colorBgBase,
                            border: `1px dashed ${token.colorBorder}`,
                            borderRadius: token.borderRadius,
                            marginTop: '0.25rem',
                            padding: '0.75rem',
                        }}
                    >
                        {FINANCIAL_STATEMENT_SECTIONS.map((section, index) => (
                            <Form.Item
                                key={section.key}
                                label={section.label}
                                name={['lineItems', section.key]}
                                style={{
                                    marginBottom: index === FINANCIAL_STATEMENT_SECTIONS.length - 1 ? 0 : '0.75rem',
                                }}
                            >
                                <Select
                                    allowClear
                                    maxCount={5}
                                    mode='multiple'
                                    optionFilterProp='label'
                                    options={[...new Set(section.lineItems)].map((lineItem) => ({
                                        label: formatFinancialLineItemLabel(lineItem),
                                        value: lineItem,
                                    }))}
                                    placeholder={`Select Up To Five ${section.label.toLocaleLowerCase()} Line Items...`}
                                    size='small'
                                />
                            </Form.Item>
                        ))}
                    </div>
                </div>
            </Form>
        </Modal>
    );
};

export default FinancialStatementPreferencesModal;
